/* -------------------------- Design imports start -------------------------- */
import MultilevelDrawer from '../../../components/layout/MultilevelDrawer'

import { Alert, AlertTitle, Grid } from '@mui/material'
import '../utils/styles.css'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { useEffect, type ReactElement } from 'react'
import ReactDiffViewer from 'react-diff-viewer-continued'
import { useTranslation } from 'react-i18next'

import styles from '../../../components/inputs/utils/infoField.module.css'
import LogTool from '../../../logger/logTools'
import { type Article } from '../../Item/utils/types'
import { cubic_symbol } from '../../Item/utils/functions'
import { type VersionComparison, VersionFieldDiff } from "../utils/types";
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: Article | undefined
    setState: React.Dispatch<React.SetStateAction<Article | undefined>>
  }
  versionDifferences: VersionComparison | undefined
  onConfirm: () => void
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function CompareVersionDrawer(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const { open, setOpen, versionDifferences } = props
  const log = new LogTool({ context: 'CompareVersionDrawer', enable: false, logLevel: 'warn' })
  const { t } = useTranslation(['common'])
  /* --------------------------- Non state data end --------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = () => {
    setOpen(false)
  }
  log.debug('versionDifferences', versionDifferences)

  // Format measurement values: remove spaces after decimal point and convert unit names
  const formatMeasurementValue = (value: string): string => {
    if (!value) return value
    
    // Remove space after decimal point (e.g., "0. 25m" -> "0.25m")
    let formatted = value.replace(/(\d)\.\s+(\d)/g, '$1.$2')
    
    // Convert unit names (e.g., "cubic_meter" -> "m³", "cubic_millimeter" -> "mm³")
    formatted = formatted.replace(/cubic_meter/g, 'm³')
    formatted = formatted.replace(/cubic_decimeter/g, 'dm³')
    formatted = formatted.replace(/cubic_centimeter/g, 'cm³')
    formatted = formatted.replace(/cubic_millimeter/g, 'mm³')
    
    return formatted
  }

  // Extract file names from ArticleFile diff
  const extractFileNamesFromDiff = (diff: string): string => {
    try {
      // Try to parse the diff as JSON (using [\s\S] instead of . with s flag for ES2017 compatibility)
      const jsonMatch = diff.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0])
        if (Array.isArray(parsed)) {
          return parsed.map((file: any) => file.name || file.key || 'Unknown file').join(', ')
        } else if (parsed && typeof parsed === 'object') {
          // Handle single file or object with files array
          if (parsed.name) {
            return parsed.name
          } else if (Array.isArray(parsed.files)) {
            return parsed.files.map((file: any) => file.name || file.key || 'Unknown file').join(', ')
          } else if (parsed.articlefile) {
            // Handle nested articlefile property
            const articleFile = parsed.articlefile
            if (Array.isArray(articleFile)) {
              return articleFile.map((file: any) => file.name || file.key || 'Unknown file').join(', ')
            } else if (articleFile && typeof articleFile === 'object' && articleFile.name) {
              return articleFile.name
            }
          }
        }
      }
      
      // Fallback: try to extract from ArticleFileobject pattern
      const articleFileMatch = diff.match(/ArticleFileobject\((\d+)\)/i)
      if (articleFileMatch) {
        return `${articleFileMatch[1]} file(s)`
      }
    } catch (error) {
      log.debug('Error extracting file names from diff:', error)
    }
    
    return diff
  }

  const getFormattedStringFromJsonDiff = (jsonDiff: string): string => {
    log.debug('jsonDiff', jsonDiff)
    try {
      const jsonDiffObject = JSON.parse(jsonDiff)
      log.debug('jsonDiffObject', jsonDiffObject)
      let formattedString = ''
      if (typeof jsonDiffObject === 'number') {
        return jsonDiff.toString()
      }
      for (const key in jsonDiffObject) {
        if (Object.prototype.hasOwnProperty.call(jsonDiffObject, key)) {
          if (
            (jsonDiffObject[key] !== null &&
              jsonDiffObject[key] !== undefined &&
              jsonDiffObject[key] !== '' &&
              key === 'process') ||
            key === 'category'
          ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/restrict-plus-operands
            formattedString += `${t('item:content.label.' + key.toLowerCase())}: ${t('item:content.label.selectProcess.' + jsonDiffObject[key].toLowerCase())},\n`
          } else {
            if (
              jsonDiffObject[key] !== null &&
              jsonDiffObject[key] !== undefined &&
              jsonDiffObject[key] !== ''
            ) {
              let value = jsonDiffObject[key]
              
              // Format measurement values
              if (typeof value === 'string') {
                value = formatMeasurementValue(value)
              }
              
              formattedString += `${t('item:content.label.' + key.toLowerCase())}: ${value},\n`
            }
          }
        } else {
          return jsonDiff
        }
      }

      // Entferne das letzte Komma und Leerzeichen
      formattedString = formattedString.slice(0, -2)

      return formattedString
    } catch (error) {
      return jsonDiff
    }
  }

  const getJsonStringFromDiff = (diff: string, delOrIns: 'del' | 'ins'): string => {
    if (delOrIns === 'del') {
      const html = document.createElement('div')
      html.innerHTML = diff
      html.querySelectorAll('del').forEach(del => del.replaceWith(del.textContent ))
      html.querySelectorAll('ins').forEach(ins => ins.remove())
      let textContent = html.textContent || ''
      textContent = textContent.replace(/\*/g, '')
      textContent = textContent.replace(/\+/g, '')
      textContent = textContent.replace(/'/g, '"')
      textContent = textContent.replace(/\s+/g, '')
      log.debug('textContent', textContent)
      return getFormattedStringFromJsonDiff(textContent)
    }
    if (delOrIns === 'ins') {
      const html = document.createElement('div')
      html.innerHTML = diff
      html.querySelectorAll('ins').forEach(ins => ins.replaceWith(ins.textContent ))
      html.querySelectorAll('del').forEach(del => del.remove())
      let textContent = html.textContent || ''
      textContent = textContent.replace(/\*/g, '')
      textContent = textContent.replace(/\+/g, '')
      textContent = textContent.replace(/'/g, '"')
      textContent = textContent.replace(/\s+/g, '')
      log.debug('textContent', textContent)
      return getFormattedStringFromJsonDiff(textContent)
    }
    return ''
  }

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  // Format date/time strings to be more readable
  const formatDateTimeString = (value: string): string => {
    if (!value) return value
    
    // Match patterns like "Dec.22,2025,10:47a.m.(timesincethatdate:44minutes)(timesincethatdate:30minutes)"
    const timeSincePattern = /\(timesincethatdate:([^)]+)\)/g
    const timeSinceMatches = [...value.matchAll(timeSincePattern)]
    
    // Remove time since patterns from the string
    let cleanedValue = value.replace(timeSincePattern, '').trim()
    
    // If we have time since information, format it nicely
    if (timeSinceMatches.length > 0) {
      // Extract the first time since value (usually the most relevant one)
      const timeSinceText = timeSinceMatches[0][1]
      
      // Format the date/time part: "Dec.22,2025,10:47a.m." -> "Dec. 22, 2025, 10:47 a.m."
      cleanedValue = cleanedValue
        .replace(/\.(\d)/g, '. $1') // Add space after period before number: "Dec." -> "Dec. "
        .replace(/(\d),(\d{4})/g, '$1, $2') // Add space after comma before year: "22,2025" -> "22, 2025"
        .replace(/(\d{4}),(\d)/g, '$1, $2') // Add space after year comma: "2025,10" -> "2025, 10"
        .replace(/(\d):(\d)/g, '$1:$2') // Keep time format: "10:47"
        .replace(/(\d)([ap])\.m\./gi, '$1 $2.m.') // Add space before a.m./p.m.: "10:47a.m." -> "10:47 a.m."
      
      // Return formatted string with time since info
      return `${cleanedValue} (${timeSinceText})`
    }
    
    // If no time since info, just clean up the basic formatting
    return cleanedValue
      .replace(/\.(\d)/g, '. $1')
      .replace(/(\d),(\d{4})/g, '$1, $2')
      .replace(/(\d{4}),(\d)/g, '$1, $2')
      .replace(/(\d)([ap])\.m\./gi, '$1 $2.m.')
  }

  const renderDiffObject = (diff: string): boolean | ReactElement => {
    const oldValue = getJsonStringFromDiff(diff, 'del')
    const newValue = getJsonStringFromDiff(diff, 'ins')
    log.debug('newValue', newValue, oldValue, 'oldValue')
    if (oldValue === newValue) {
      return false
    }
    const filterValueList = ['None', 'add', 'changedto', 'Falsechangedto', 'del', '']
    const newValueList = newValue.split(':').filter((val: string) => !filterValueList.includes(val))
    const oldValueList = oldValue.split(':').filter((val: string) => !filterValueList.includes(val))
    if (newValueList.length === 0 && oldValueList.length === 0) {
      return false
    }
    
    // Format date/time strings if they contain time since information
    let formattedOldValue = formatDateTimeString(oldValueList.join(':'))
    let formattedNewValue = formatDateTimeString(newValueList.join(':'))
    
    // Format measurement values: remove spaces after decimal point and convert units
    formattedOldValue = formatMeasurementValue(formattedOldValue)
    formattedNewValue = formatMeasurementValue(formattedNewValue)
    
    return (
      <ReactDiffViewer
        oldValue={formattedOldValue}
        newValue={formattedNewValue}
        splitView={true}
        disableWordDiff={true}
        styles={{ wordDiff: { display: 'none' } }}
      />
    )
  }

  const renderDiffObjectForFullAmount = (diff: string) => {
    const oldValue = getJsonStringFromDiff(diff, 'del')
    const newValue = getJsonStringFromDiff(diff, 'ins')
    const values = newValue
      .split(': ')
      .slice(1)
      .map(val => parseInt(val.trim())) // Extrahiere und konvertiere die Zahlenwerte
    const sum = values.reduce((acc, curr) => acc + curr, 0) // Berechne die Summe der Zahlen

    log.debug('newValue', newValue)
    if (oldValue === newValue) {
      return
    }
    return (
      <ReactDiffViewer
        oldValue={oldValue}
        newValue={sum.toString()} // Setze die Summe als neuen Wert
        splitView={true}
        disableWordDiff={true}
        styles={{ wordDiff: { display: 'none' } }}
      />
    )
  }

  const renderVersionDifferences = (compare_data: VersionComparison['compare_data']) => {
    const filteredData = compare_data.filter(
      compare => compare.field !== 'items.Article.root_account'
    )
    return filteredData.map((compare: VersionFieldDiff): ReactElement => {
      const field = compare.field.split('.').pop()
      if (!field) {
        log.error("Field is undefined")
        return <></>
      }
      const indexOfGreaterThan = field.indexOf('>')
      const fieldWithoutSpecialChars =
        indexOfGreaterThan !== -1 ? field.substring(0, indexOfGreaterThan) : field

      // Überprüfen, ob das Diff Tags für Löschungen oder Einfügungen enthält
      const hasDelInsTags = compare.diff.includes('<del>') || compare.diff.includes('<ins>')

      // Wenn keine Löschungs- oder Einfügungstags vorhanden sind, wird kein Label erzeugt
      if (!hasDelInsTags) {
        return (
          <div className={`${styles.root} ${styles.fullwidth}`}>
            <span className={styles.value_text}>{renderDiffObject(compare.diff)}</span>
          </div>
        )
      }

      if (compare.field === '<ManyToOneRel: logistics.storagearticle>') {
        return (
          <div className={`${styles.root} ${styles.fullwidth}`}>
            <label className={styles.label}>{t('item:content.label.amountInStock')}</label>
            <div className={styles.value_container}>
              <span className={styles.value_text}>
                {renderDiffObjectForFullAmount(compare.diff)}
              </span>
            </div>
          </div>
        )
      }

      // Wenn Löschungs- oder Einfügungstags vorhanden sind, wird das Label erzeugt
      // Convert snake_case to camelCase and camelCase to snake_case for translation keys
      const fieldCamelCase = fieldWithoutSpecialChars.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
      // Convert camelCase to snake_case, handling the first character correctly
      const fieldSnakeCase = fieldWithoutSpecialChars
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .toLowerCase()
      
      // Helper function to check if translation exists
      const getTranslation = (key: string): string | null => {
        try {
          const translation = t(key)
          // If translation equals the key (with or without namespace), it means no translation was found
          // i18next returns the key if translation is missing, sometimes with or without namespace
          const keyWithoutNamespace = key.split(':').pop() || key
          // Check if translation is the same as the key (indicating missing translation)
          if (translation === key || translation === keyWithoutNamespace) {
            return null
          }
          // Check if translation looks like a missing key (starts with namespace or content.label)
          if (translation.includes('content.label.') && translation.split('.').length > 2) {
            return null
          }
          return translation
        } catch {
          return null
        }
      }
      
      // Special handling for articlefile field
      if (fieldWithoutSpecialChars.toLowerCase() === 'articlefile' || fieldWithoutSpecialChars.toLowerCase() === 'files') {
        // Extract file names from old and new values
        const oldDiffValue = getJsonStringFromDiff(compare.diff, 'del')
        const newDiffValue = getJsonStringFromDiff(compare.diff, 'ins')
        const oldFileNames = extractFileNamesFromDiff(oldDiffValue)
        const newFileNames = extractFileNamesFromDiff(newDiffValue)
        
        // Also try to extract from the formatted string
        const oldFormatted = getFormattedStringFromJsonDiff(oldDiffValue)
        const newFormatted = getFormattedStringFromJsonDiff(newDiffValue)
        const oldFromFormatted = extractFileNamesFromDiff(oldFormatted)
        const newFromFormatted = extractFileNamesFromDiff(newFormatted)
        
        const finalOldNames = oldFromFormatted !== oldFormatted ? oldFromFormatted : oldFileNames
        const finalNewNames = newFromFormatted !== newFormatted ? newFromFormatted : newFileNames
        
        // Create a custom diff viewer for file names
        if (finalOldNames !== finalNewNames || finalOldNames !== oldDiffValue || finalNewNames !== newDiffValue) {
          return (
            <div className={`${styles.root} ${styles.fullwidth}`}>
              <label className={styles.label}>
                {t('common:content.label.articleFile') || t('item:content.label.articleFile') || 'Article File'}
              </label>
              <div className={styles.value_container}>
                <span className={styles.value_text}>
                  <ReactDiffViewer
                    oldValue={finalOldNames || 'No files'}
                    newValue={finalNewNames || 'No files'}
                    splitView={true}
                    disableWordDiff={true}
                    styles={{ wordDiff: { display: 'none' } }}
                  />
                </span>
              </div>
            </div>
          )
        }
      }
      
      // Try different translation keys in priority order
      // Priority: common camelCase > common snake_case > item original > item snake_case > item camelCase > field name
      const translationKey = 
        getTranslation(`common:content.label.${fieldCamelCase}`) ||
        getTranslation(`common:content.label.${fieldSnakeCase}`) ||
        getTranslation(`item:content.label.${fieldWithoutSpecialChars}`) ||
        getTranslation(`item:content.label.${fieldSnakeCase}`) ||
        getTranslation(`item:content.label.${fieldCamelCase}`) ||
        fieldWithoutSpecialChars
      
      // Debug logging (can be removed in production)
      if (translationKey === fieldWithoutSpecialChars) {
        log.debug('Translation not found for field:', fieldWithoutSpecialChars, {
          fieldCamelCase,
          fieldSnakeCase,
          triedKeys: [
            `common:content.label.${fieldCamelCase}`,
            `common:content.label.${fieldSnakeCase}`,
            `item:content.label.${fieldWithoutSpecialChars}`,
            `item:content.label.${fieldSnakeCase}`,
            `item:content.label.${fieldCamelCase}`
          ]
        })
      }
      
      return (
        <div className={`${styles.root} ${styles.fullwidth}`}>
          {renderDiffObject(compare.diff) !== false && (
            <label className={styles.label}>
              {translationKey}
            </label>
          )}
          <div className={styles.value_container}>
            <span className={styles.value_text}>{renderDiffObject(compare.diff)}</span>
          </div>
        </div>
      )
    })
  }

  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <MultilevelDrawer
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      size="big"
      title={t('common:content.label.compareVersions') }
      confirmButtonProps={{
        // hide the confirm button
        hidden: true,
        text: '',
        disabled: true,
        // do nothing on confirm
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onConfirm: () => {},
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {versionDifferences && versionDifferences.compare_data.length > 0 ? (
            renderVersionDifferences(versionDifferences.compare_data)
          ) : (
            <Alert severity="info" color="warning">
              <AlertTitle>Info</AlertTitle>
              {t('item:feedback.info.noChangesInVersion')}
            </Alert>
          )}
        </Grid>
      </Grid>
    </MultilevelDrawer>
  )
}
